import {
  Bot,
  CheckCircle2,
  Eye,
  ExternalLink,
  Layers,
  Lock,
  MapPin,
  Newspaper,
  Send,
  ShieldAlert,
  Sparkles,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { PostPicker } from '../components/PostPicker';
import { Spinner } from '../components/ui/spinner';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/toast';
import { AuthedImage, AuthedVideo } from '../components/AuthedMedia';
import {
  type EyewitnessListItem,
  type EyewitnessStatus,
  eyewitnessMediaPath,
  useEyewitnessMedia,
  useEyewitnessReport,
  useEyewitnessReports,
  useModerateEyewitness,
} from '../lib/api/eyewitness';
import { fmtDateTime, fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { usePersistedState } from '../lib/usePersistedState';
import { cn } from '../lib/utils';

const STATUS_FILTERS: { key: EyewitnessStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'verified', label: 'Verified' },
  { key: 'published', label: 'Published' },
  { key: 'internal', label: 'Internal' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_TONE: Record<EyewitnessStatus, { bg: string; text: string; ring: string; label: string }> = {
  new:        { bg: 'bg-brand/10',   text: 'text-brand',   ring: 'ring-brand/30',   label: 'New' },
  reviewed:   { bg: 'bg-info/10',    text: 'text-info',    ring: 'ring-info/30',    label: 'Reviewed' },
  verified:   { bg: 'bg-success/10', text: 'text-success', ring: 'ring-success/30', label: 'Verified' },
  published:  { bg: 'bg-brand/15',   text: 'text-brand',   ring: 'ring-brand/40',   label: 'Published' },
  internal:   { bg: 'bg-surface-tinted', text: 'text-ink-muted', ring: 'ring-line', label: 'Internal' },
  rejected:   { bg: 'bg-danger/10',  text: 'text-danger',  ring: 'ring-danger/30',  label: 'Rejected' },
};

export default function Eyewitness(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  // Persisted: editors usually return to the same triage queue; default 'new'
  // is the right first-visit choice but a sticky pick saves clicks on revisits.
  const [statusFilter, setStatusFilter] = usePersistedState<EyewitnessStatus | 'all'>(
    'eyewitness:statusFilter',
    'new',
  );
  const [openId, setOpenId] = useState<string | null>(null);

  // Deep-link support: arrive via `/eyewitness?openId=<id>` (e.g. from the
  // editorial inbox) and the report dialog opens automatically. Mirrored
  // back into the URL when the dialog opens/closes so a refresh or share
  // preserves the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const fromUrl = searchParams.get('openId');
    if (fromUrl && fromUrl !== openId) setOpenId(fromUrl);
    // Intentionally only watching `searchParams.get('openId')` — we don't
    // want this effect to fire when local `openId` flips, only when the
    // URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('openId')]);

  const list = useEyewitnessReports({
    status: statusFilter === 'all' ? undefined : statusFilter,
    locale,
    limit: 50,
  });

  const items = list.data?.items ?? [];

  return (
    <>
      <Topbar title="Eyewitness" subtitle={`Locale ${locale.toUpperCase()}`} />
      <PageHeader
        title="Eyewitness"
        eyebrow="Newsroom · Civic Reports"
        subtitle={
          list.isLoading
            ? 'Loading…'
            : `${items.length} report${items.length === 1 ? '' : 's'} in ${
                statusFilter === 'all' ? 'queue' : `"${statusFilter}"`
              } · ${locale.toUpperCase()}`
        }
      />
      <div className="space-y-4 px-6 pb-10">
        {/* Status filter chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setStatusFilter(f.key)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] transition-colors',
                  active
                    ? 'border-brand bg-brand text-white shadow-sm'
                    : 'border-line bg-surface text-ink-muted hover:bg-surface-tinted hover:text-ink',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {list.isLoading ? (
          <Card className="p-6">
            <Spinner label="Loading…" />
          </Card>
        ) : items.length === 0 ? (
          <Empty
            title="Nothing here"
            description={
              statusFilter === 'new'
                ? 'No new submissions to triage. Reports from the mobile apps will land here when ingest comes online.'
                : `No reports with status "${statusFilter}" in ${locale.toUpperCase()}.`
            }
          />
        ) : (
          <ul className="space-y-2">
            {items.map((r) => (
              <ReportCard key={r.id} report={r} onOpen={() => setOpenId(r.id)} />
            ))}
          </ul>
        )}
      </div>

      <ReviewDialog
        id={openId}
        onClose={() => {
          setOpenId(null);
          // Strip ?openId= from the URL when the dialog closes so a back-button
          // navigation doesn't immediately re-open it. Other params on the URL
          // (none today, but future-proof) are preserved.
          if (searchParams.has('openId')) {
            const next = new URLSearchParams(searchParams);
            next.delete('openId');
            setSearchParams(next, { replace: true });
          }
        }}
      />
    </>
  );
}

function ReportCard({
  report,
  onOpen,
}: {
  report: EyewitnessListItem;
  onOpen: () => void;
}): JSX.Element {
  const tone = STATUS_TONE[report.status];
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-start gap-3 rounded-lg border border-line bg-surface p-3 text-left shadow-card transition-all hover:border-line-strong hover:shadow-elevated"
      >
        {/* Status pill (vertical stripe + label) */}
        <div className="flex min-w-[80px] shrink-0 flex-col items-start gap-1.5">
          <span
            className={cn(
              'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ring-1',
              tone.bg,
              tone.text,
              tone.ring,
            )}
          >
            {tone.label}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-subtle">
            {report.locale}
          </span>
          {report.corroborationCount > 1 ? (
            <span
              className="rounded-sm bg-warning/10 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-warning"
              title={`${report.corroborationCount} reports near this incident`}
            >
              ×{report.corroborationCount} echo
            </span>
          ) : null}
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1 space-y-1.5">
          {report.aiSummary ? (
            <p className="flex items-start gap-1.5 text-[12.5px] italic leading-snug text-ink-muted">
              <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
              <span className="line-clamp-1">{report.aiSummary}</span>
            </p>
          ) : null}
          <p className="line-clamp-3 text-[13.5px] leading-snug text-ink">
            {report.text}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-subtle">
            {report.submitter ? (
              <span className="flex items-center gap-1">
                <UserIcon className="h-3 w-3" />
                <span className="normal-case tracking-normal">
                  {report.submitter.name ?? report.submitter.email ?? '—'}
                </span>
              </span>
            ) : null}
            {report.locationLabel ? (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span className="normal-case tracking-normal truncate max-w-[14rem]">
                  {report.locationLabel}
                </span>
              </span>
            ) : null}
            <span title={fmtDateTime(report.happenedAt)}>
              <span className="opacity-60">happened</span>{' '}
              <span className="normal-case tracking-normal">{fmtRelative(report.happenedAt)}</span>
            </span>
            {report.aiSuggestedCategory ? (
              <Badge variant="brand" className="font-mono text-[9.5px]">
                {report.aiSuggestedCategory}
              </Badge>
            ) : null}
          </div>
        </div>

        <Eye className="mt-1 h-4 w-4 shrink-0 text-ink-subtle opacity-0 transition-all group-hover:opacity-100 group-hover:text-brand" />
      </button>
    </li>
  );
}

function ReviewDialog({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}): JSX.Element {
  const detail = useEyewitnessReport(id);
  const media = useEyewitnessMedia(id);
  const moderate = useModerateEyewitness(id);
  const { showToast } = useToast();
  const [notes, setNotes] = useState('');
  const [publishMode, setPublishMode] = useState(false);
  const [publishSlug, setPublishSlug] = useState('');

  // Sync local notes with whatever the server has on open. Re-keyed by `id` so
  // editor edits don't leak between reports.
  const data = detail.data ?? null;
  const [lastId, setLastId] = useState<string | null>(null);
  if (data && data.id !== lastId) {
    setLastId(data.id);
    setNotes(data.moderatorNotes ?? '');
    setPublishMode(false);
    setPublishSlug('');
  }

  const handle = async (status: 'reviewed' | 'verified' | 'internal' | 'rejected') => {
    if (!id) return;
    try {
      await moderate.mutateAsync({ status, notes: notes.trim() || null });
      showToast({
        title: `Marked ${status}`,
        description: `Report #${id}`,
        variant: status === 'rejected' ? 'danger' : 'success',
      });
      onClose();
    } catch (err) {
      showToast({
        title: 'Update failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  };

  const handlePublish = async () => {
    if (!id) return;
    const slug = publishSlug.trim();
    if (!slug) {
      showToast({
        title: 'Post slug required',
        description: 'Paste the slug of the VON post that covers this report.',
        variant: 'danger',
      });
      return;
    }
    try {
      await moderate.mutateAsync({
        status: 'published',
        notes: notes.trim() || null,
        publishedPostSlug: slug,
      });
      showToast({
        title: 'Published',
        description: `Report #${id} linked to ${slug}`,
        variant: 'success',
      });
      onClose();
    } catch (err) {
      showToast({
        title: 'Publish failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <p className="eyebrow">
            <Eye className="h-3 w-3" /> Eyewitness review
          </p>
          <DialogTitle>
            {data ? `Report #${data.id}` : 'Loading…'}
          </DialogTitle>
          <DialogDescription>
            Submission from {data?.submitter?.name ?? data?.submitter?.email ?? 'unknown user'}
            {data ? ` · ${fmtRelative(data.createdAt)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Scroll region: claims the flex middle so DialogHeader stays pinned
            on top and DialogFooter stays pinned at the bottom. Negative
            horizontal margin + padding compensates so the scrollbar doesn't
            sit hard against the dialog edge. */}
        {detail.isLoading || !data ? (
          <div className="flex-1 overflow-y-auto py-8">
            <Spinner label="Loading report…" />
          </div>
        ) : (
          <div className="-mx-2 grid flex-1 gap-4 overflow-y-auto px-2">
            {/* AI summary banner */}
            {data.aiSummary ? (
              <div className="flex gap-2 rounded-lg border border-brand/20 bg-brand-soft px-3 py-2.5">
                <Bot className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-brand">
                    AI summary
                  </p>
                  <p className="mt-0.5 text-[13.5px] italic text-ink">{data.aiSummary}</p>
                  {data.aiSuggestedCategory ? (
                    <p className="mt-1.5 flex items-center gap-1.5">
                      <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-subtle">
                        Suggested category:
                      </span>
                      <Badge variant="brand" className="font-mono text-[10px]">
                        {data.aiSuggestedCategory}
                      </Badge>
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Full text */}
            <div className="space-y-1">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                Submitted text
              </p>
              <p className="whitespace-pre-wrap rounded-md border border-line bg-surface px-3 py-2.5 text-[14px] leading-relaxed text-ink">
                {data.text}
              </p>
            </div>

            {/* Media gallery — only renders the heading once we know there's
                something to show. Empty state stays quiet. */}
            {media.data && media.data.items.length > 0 ? (
              <div className="space-y-1.5">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                  Media · {media.data.items.length}
                </p>
                <MediaGallery reportId={data.id} items={media.data.items} />
              </div>
            ) : null}

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Meta label="Status" value={STATUS_TONE[data.status].label} />
              <Meta label="Locale" value={data.locale.toUpperCase()} />
              <Meta label="Happened" value={fmtRelative(data.happenedAt)} title={fmtDateTime(data.happenedAt)} />
              <Meta label="Submitted" value={fmtRelative(data.createdAt)} title={fmtDateTime(data.createdAt)} />
              {data.locationLabel ? (
                <Meta label="Location" value={data.locationLabel} className="col-span-2" />
              ) : null}
              {data.locationLat && data.locationLng ? (
                <div className="col-span-2 flex items-center gap-2 rounded-md bg-surface-tinted/40 px-3 py-2">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-brand" />
                  <a
                    href={`https://www.google.com/maps?q=${data.locationLat},${data.locationLng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 font-mono text-[11.5px] text-brand hover:underline"
                  >
                    {data.locationLat}, {data.locationLng}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              ) : null}
            </div>

            {/* Signal row */}
            <div className="flex flex-wrap items-center gap-2">
              {data.corroborationCount > 1 ? (
                <span className="flex items-center gap-1.5 rounded-md bg-warning/10 px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-warning">
                  <Layers className="h-3 w-3" />
                  {data.corroborationCount} corroborating reports
                  {data.corroboratedAt ? <span className="opacity-70">· cluster confirmed</span> : null}
                </span>
              ) : null}
              {data.aiDuplicateOfId ? (
                <span className="flex items-center gap-1.5 rounded-md bg-surface-tinted px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
                  <Sparkles className="h-3 w-3" />
                  Possible duplicate of #{data.aiDuplicateOfId}
                </span>
              ) : null}
            </div>

            {/* Submitter */}
            <div className="rounded-md border border-line bg-surface-tinted/40 px-3 py-2">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                Submitter
              </p>
              <p className="mt-0.5 text-[13px] text-ink">
                <span className="font-medium">{data.submitter?.name ?? '—'}</span>
                {data.submitter?.email ? (
                  <span className="ml-2 text-ink-muted">{data.submitter.email}</span>
                ) : null}
              </p>
            </div>

            {/* Reviewer history */}
            {data.reviewedAt ? (
              <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-subtle">
                Last reviewed {fmtRelative(data.reviewedAt)}
                {data.reviewedBy?.name ? ` by ${data.reviewedBy.name}` : ''}
              </p>
            ) : null}

            {/* Notes */}
            <div className="space-y-1">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                Moderator notes
              </p>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Internal-only — never shown to the submitter."
              />
            </div>

            {/* Publish panel — shown only after the editor clicks Publish in
                the footer. Search-as-you-type picker over published posts in
                the report's locale; on select, server resolves the slug to a
                post id and links the report. */}
            {publishMode ? (
              <div className="space-y-2 rounded-md border border-brand/30 bg-brand-soft/40 px-3 py-2.5">
                <p className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-brand">
                  <Newspaper className="h-3 w-3" /> Linked VON post
                </p>
                <p className="text-[12.5px] leading-snug text-ink-muted">
                  Find the post you've already published that covers this story.
                  Searching in <strong className="text-ink">{data.locale.toUpperCase()}</strong>.
                </p>
                <PostPicker
                  type="post"
                  locale={data.locale}
                  value={publishSlug}
                  onChange={(slug) => setPublishSlug(slug)}
                  placeholder="Search VON posts…"
                />
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {data ? (
            publishMode ? (
              // While the publish panel is open, the only relevant actions are
              // confirm + cancel. The other buttons (Reject / Internal / etc.)
              // would conflict with the slug the editor just typed.
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPublishMode(false);
                    setPublishSlug('');
                  }}
                  disabled={moderate.isPending}
                  className="sm:mr-auto"
                >
                  Cancel publish
                </Button>
                <Button
                  type="button"
                  onClick={() => void handlePublish()}
                  disabled={moderate.isPending || !publishSlug.trim()}
                >
                  <Send className="h-3.5 w-3.5" />
                  {moderate.isPending ? 'Publishing…' : 'Confirm publish'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => void handle('rejected')}
                  disabled={moderate.isPending}
                  className="sm:mr-auto"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Reject
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void handle('internal')}
                  disabled={moderate.isPending}
                >
                  <Lock className="h-3.5 w-3.5" />
                  Internal only
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handle('reviewed')}
                  disabled={moderate.isPending}
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Mark reviewed
                </Button>
                {/* Verified reports are the natural source for "Publish"; we
                    show the button at every status so editors can also publish
                    straight from a 'new' or 'reviewed' state if they want. */}
                {data.status === 'published' ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPublishMode(true)}
                    disabled={moderate.isPending}
                  >
                    <Newspaper className="h-3.5 w-3.5" />
                    Publish
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => void handle('verified')}
                  disabled={moderate.isPending}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Verify
                </Button>
              </>
            )
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meta({
  label,
  value,
  title,
  className,
}: {
  label: string;
  value: string;
  title?: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('rounded-md bg-surface-tinted/40 px-3 py-2', className)}>
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
        {label}
      </p>
      <p className="mt-0.5 truncate text-[13px] text-ink" title={title ?? value}>
        {value}
      </p>
    </div>
  );
}

function MediaGallery({
  reportId,
  items,
}: {
  reportId: string;
  items: Array<{ id: string; kind: 'image' | 'video'; mimeType: string; sizeBytes: number }>;
}): JSX.Element {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightbox = lightboxIndex !== null ? items[lightboxIndex] : null;

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {items.map((m, i) => {
          const path = eyewitnessMediaPath(reportId, m.id);
          if (m.kind === 'video') {
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="group relative aspect-square overflow-hidden rounded-md border border-line bg-surface-tinted"
              >
                <AuthedVideo path={path} className="h-full w-full object-cover" controls={false} />
                <span className="pointer-events-none absolute inset-0 flex items-end justify-start bg-gradient-to-t from-black/55 to-transparent p-2">
                  <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white">
                    Video · {(m.sizeBytes / 1024 / 1024).toFixed(1)} MB
                  </span>
                </span>
              </button>
            );
          }
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="group aspect-square overflow-hidden rounded-md border border-line bg-surface-tinted transition-shadow hover:shadow-elevated"
            >
              <AuthedImage
                path={path}
                alt={`Eyewitness photo ${i + 1}`}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            </button>
          );
        })}
      </div>

      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightboxIndex(null)}>
        <DialogContent size="xl" className="overflow-hidden p-0">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle>
              {lightbox?.kind === 'video' ? 'Video' : 'Photo'}
              {lightboxIndex !== null ? ` · ${lightboxIndex + 1} of ${items.length}` : ''}
            </DialogTitle>
            <DialogDescription>
              {lightbox
                ? `${lightbox.mimeType} · ${(lightbox.sizeBytes / 1024 / 1024).toFixed(2)} MB`
                : ''}
            </DialogDescription>
          </DialogHeader>
          {lightbox ? (
            <div className="grid place-items-center bg-black p-2">
              {lightbox.kind === 'video' ? (
                <AuthedVideo
                  path={eyewitnessMediaPath(reportId, lightbox.id)}
                  className="max-h-[70vh] w-full"
                />
              ) : (
                <AuthedImage
                  path={eyewitnessMediaPath(reportId, lightbox.id)}
                  alt="Eyewitness media"
                  className="max-h-[70vh] w-auto object-contain"
                />
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
